import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Packer,
  PageNumber,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
  UnderlineType,
} from 'docx'
import { saveAs } from 'file-saver'
import React from 'react'
import {
  getPlykSuffixByTab,
  isDelegateFeedbackTab,
  toDisplayDocumentNumber,
} from '../feedbackBoardType'

function getUserPositionName(user) {
  return (
    user?.position_name ||
    user?.position?.name ||
    user?.positionName ||
    user?.position_detail?.name ||
    user?.position_detail?.position_name ||
    user?.positionNameInternal ||
    (typeof user?.position === 'string' ? user.position : '') ||
    ''
  )
}

const ExportDocx = ({
  feedbackDetailData,
  finalUserList,
  countAllUser,
  countAgree,
  countDisAgree,
  countOther,
  totalPeopleCount,
  resultsVoting,
  positionByUsername = {},
  feedbackTab,
}) => {
  const {
    title,
    assignees,
    purpose,
    feedbackComments,
    attachedDocuments,
  } = feedbackDetailData
  const isDelegateTab = isDelegateFeedbackTab(feedbackTab)
  const plykSuffix = getPlykSuffixByTab(feedbackTab)
  const bbykSuffix = plykSuffix.replace('PLYK', 'BBYK')
  const thykSuffix = plykSuffix.replace('PLYK', 'THYK')
  const displayDocumentNumber = toDisplayDocumentNumber(
    feedbackDetailData.documentNumber
  )

  // Enrich assignees with user info
  assignees?.forEach(el => {
    const user = finalUserList?.find(
      u => (u.user_name || u.username) === el.assignee_code
    )
    if (user) {
      el.name_uppercase = user.name_uppercase
      el.position = getUserPositionName(user)
    }
  })

  // Enrich resultsVoting with user info
  resultsVoting?.forEach(comment => {
    comment?.forEach(detail => {
      const user = finalUserList?.find(
        u => (u.user_name || u.username) === detail.feedbackUsername
      )
      if (user) {
        detail.name_uppercase = user.name_uppercase
        detail.position = getUserPositionName(user)
        detail.positionCode = user.position?.code
      }
    })
  })

  // Tally votes per content item
  const results = resultsVoting?.map(feedbackList => {
    let cAgree = 0,
      cDisAgree = 0,
      cOther = 0
    feedbackList?.forEach(fb => {
      if (fb.voteType === 'AGREE') cAgree++
      else if (fb.voteType === 'DISAGREE') cDisAgree++
      else cOther++
    })
    return {
      content: feedbackList?.[0]?.content,
      feedbackList,
      countAgree: cAgree,
      countDisAgree: cDisAgree,
      countOther: cOther,
    }
  })

  // Helpers
  const formatNumber = num => (num < 10 ? `0${num}` : `${num}`)
  const normalizeUsername = value =>
    (value || '').toString().trim().toLowerCase()
  const isGenericDelegatePosition = position =>
    normalizeText(position) ===
    normalizeText('Nguoi dai dien phan von nha nuoc')

  const capitalizeFirstLetter = str =>
    str
      .toLowerCase()
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')

  const normalizeText = value =>
    (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\u0111/g, 'd')
      .replace(/\u0110/g, 'D')
      .toUpperCase()
      .trim()

  const decodeHtmlEntities = value =>
    (value || '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))

  const stripHtml = html =>
    decodeHtmlEntities((html || '').replace(/<\/?[^>]+(>|$)/g, ''))
      .replace(/\s+/g, ' ')
      .trim()

  const attachedDocumentsText = stripHtml(attachedDocuments || '')

  const htmlToPlainLines = html => {
    if (!html) return []

    const normalizedHtml = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<\/h[1-6]>/gi, '\n')
      .replace(/<li[^>]*>/gi, '+ ')

    const plainText = decodeHtmlEntities(
      normalizedHtml.replace(/<\/?[^>]+(>|$)/g, '')
    )
      .replace(/\u00a0/g, ' ')
      .replace(/\r\n/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()

    if (!plainText) return []

    return plainText
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
  }

  const noBorder = {
    top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  }

  const today = new Date()
  const dd = formatNumber(today.getDate())
  const mm = formatNumber(today.getMonth() + 1)
  const yyyy = today.getFullYear()

  // Build a username -> display name map from ALL available sources
  const buildNameMap = () => {
    const map = {}
    // Source 1: finalUserList
    finalUserList?.forEach(u => {
      const uname = u.user_name || u.username
      if (uname && u.name_uppercase) {
        map[uname] = capitalizeFirstLetter(u.name_uppercase)
      }
    })
    // Source 2: resultsVoting (already enriched above)
    resultsVoting?.forEach(feedbackList => {
      feedbackList?.forEach(fb => {
        if (
          fb.feedbackUsername &&
          fb.name_uppercase &&
          !map[fb.feedbackUsername]
        ) {
          map[fb.feedbackUsername] = capitalizeFirstLetter(fb.name_uppercase)
        }
      })
    })
    // Source 3: feedbackComments details
    feedbackComments?.forEach(comment => {
      comment.feedbackCommentDetails?.forEach(detail => {
        if (
          detail.feedbackUsername &&
          detail.name_uppercase &&
          !map[detail.feedbackUsername]
        ) {
          map[detail.feedbackUsername] = capitalizeFirstLetter(
            detail.name_uppercase
          )
        }
      })
    })
    return map
  }

  const buildPositionMap = () => {
    const map = {}

    const setPosition = (username, positionName) => {
      const key = normalizeUsername(username)
      if (!key || !positionName) return
      const currentPosition = map[key]
      if (
        currentPosition &&
        !isGenericDelegatePosition(currentPosition) &&
        isGenericDelegatePosition(positionName)
      ) {
        return
      }
      map[key] = positionName
    }

    // Source 0: explicit map from FeedbackBoardDetail (username -> position)
    Object.entries(positionByUsername || {}).forEach(([username, position]) => {
      setPosition(username, position)
    })

    // Source 1: finalUserList
    finalUserList?.forEach(u => {
      const positionName = getUserPositionName(u)
      setPosition(u.user_name, positionName)
      setPosition(u.username, positionName)
    })

    // Source 2: assignees
    assignees?.forEach(a => {
      const rawPos = a.position || a.positionName || a.position_name || ''
      setPosition(a.assignee_code, getUserPositionName({ position: rawPos }))
    })

    // Source 3: resultsVoting details
    resultsVoting?.forEach(feedbackList => {
      feedbackList?.forEach(fb => {
        const rawPos = fb.position || fb.positionName || fb.position_name || ''
        setPosition(
          fb.feedbackUsername,
          getUserPositionName({ position: rawPos })
        )
      })
    })

    // Source 4: feedbackComments details
    feedbackComments?.forEach(comment => {
      comment?.feedbackCommentDetails?.forEach(detail => {
        const rawPos =
          detail.position || detail.positionName || detail.position_name || ''
        setPosition(
          detail.feedbackUsername,
          getUserPositionName({ position: rawPos })
        )
      })
    })

    return map
  }

  const buildPositionByNameMap = () => {
    const map = {}

    const setPositionByName = (fullName, positionName) => {
      const key = normalizeText(fullName)
      if (!key || !positionName) return
      const currentPosition = map[key]
      if (
        currentPosition &&
        !isGenericDelegatePosition(currentPosition) &&
        isGenericDelegatePosition(positionName)
      ) {
        return
      }
      map[key] = positionName
    }

    finalUserList?.forEach(u => {
      const positionName = getUserPositionName(u)
      setPositionByName(
        u?.name_uppercase || u?.name || u?.fullName,
        positionName
      )
    })

    assignees?.forEach(a => {
      const rawPos = a?.position || a?.positionName || a?.position_name || ''
      setPositionByName(
        a?.name_uppercase || a?.name || a?.fullName,
        getUserPositionName({ position: rawPos })
      )
    })

    return map
  }

  // Lookup display name, never fall back to raw username
  const getVoterName = (fb, nameMap) => {
    return (
      nameMap[fb.feedbackUsername] ||
      (fb.name_uppercase ? capitalizeFirstLetter(fb.name_uppercase) : null) ||
      fb.name ||
      fb.fullName ||
      fb.feedbackUsername // last resort
    )
  }

  const getVoterPosition = (fb, positionMap, positionByNameMap, nameMap) => {
    const key = normalizeUsername(fb?.feedbackUsername)
    const voterNameKey = normalizeText(getVoterName(fb, nameMap))

    const matchedUser = finalUserList?.find(user => {
      const uname = normalizeUsername(user?.user_name || user?.username)
      return uname && uname === key
    })

    let resolvedPosition =
      positionMap[key] ||
      getUserPositionName(matchedUser) ||
      positionByNameMap[voterNameKey]

    if (!resolvedPosition) {
      const rawPos = fb?.position || fb?.positionName || fb?.position_name || ''
      resolvedPosition = getUserPositionName({ position: rawPos })
    }

    if (isDelegateTab) {
      const title = ''
      const pos = resolvedPosition || ''
      resolvedPosition = pos.includes(title)
        ? pos
        : pos
        ? `${title} - ${pos}`
        : title
    }

    return resolvedPosition || ''
  }

  const HDQT_VOTING_ORDER_USERNAMES = [
    'tinhnc',
    'sonla',
    'chungnd',
    'ducdt',
    'duongdh',
  ]
  const HDQT_VOTING_ORDER_NAMES = [
    'NGUYEN CANH TINH',
    'LE ANH SON',
    'NGUYEN DINH CHUNG',
    'DO TIEN DUC',
    'DO HUNG DUONG',
  ]
  const hdqtOrderByUsername = new Map(
    HDQT_VOTING_ORDER_USERNAMES.map((username, index) => [username, index + 1])
  )
  const hdqtOrderByName = new Map(
    HDQT_VOTING_ORDER_NAMES.map((name, index) => [name, index + 1])
  )

  const getHdqtOrder = (voter, nameMap) => {
    const username = voter?.feedbackUsername
    if (username && hdqtOrderByUsername.has(username)) {
      return hdqtOrderByUsername.get(username)
    }

    const normalizedName = normalizeText(getVoterName(voter, nameMap))
    if (normalizedName && hdqtOrderByName.has(normalizedName)) {
      return hdqtOrderByName.get(normalizedName)
    }

    return null
  }

  const compareVoters = (a, b, nameMap) => {
    const hdqtOrderA = getHdqtOrder(a, nameMap)
    const hdqtOrderB = getHdqtOrder(b, nameMap)
    if (hdqtOrderA !== null || hdqtOrderB !== null) {
      return (
        (hdqtOrderA ?? Number.MAX_SAFE_INTEGER) -
        (hdqtOrderB ?? Number.MAX_SAFE_INTEGER)
      )
    }

    const positionPriority = {
      CTHDTV: 1,
      CTHDQT: 1,
      CT: 1,
      TGD: 2,
      TVHDTV: 3,
      TVHDQT: 3,
    }
    const positionCodeA = normalizeText(a?.positionCode)
    const positionCodeB = normalizeText(b?.positionCode)
    const priorityA = positionPriority[positionCodeA] || 99
    const priorityB = positionPriority[positionCodeB] || 99
    if (priorityA !== priorityB) return priorityA - priorityB

    const nameA = normalizeText(getVoterName(a, nameMap))
    const nameB = normalizeText(getVoterName(b, nameMap))
    return nameA.localeCompare(nameB)
  }

  // Build unique voters ordered list
  const getAllVoters = nameMap => {
    const seen = new Set()
    const voters = []
    resultsVoting?.forEach(feedbackList => {
      feedbackList?.forEach(fb => {
        if (!seen.has(fb.feedbackUsername)) {
          seen.add(fb.feedbackUsername)
          voters.push(fb)
        }
      })
    })

    voters.sort((a, b) => compareVoters(a, b, nameMap))

    return voters
  }

  // Build voter -> per-content vote map
  const buildVoterVotesMap = () => {
    const map = {}
    resultsVoting?.forEach((feedbackList, contentIdx) => {
      feedbackList?.forEach(fb => {
        if (!map[fb.feedbackUsername]) {
          map[fb.feedbackUsername] = {}
        }
        map[fb.feedbackUsername][contentIdx] = fb
      })
    })
    return map
  }

  const isApprovedContent = result => {
    const agreeCount = result?.countAgree || 0
    const disagreeCount = result?.countDisAgree || 0

    if (isDelegateTab) {
      return agreeCount > disagreeCount
    }

    const otherCount = result?.countOther || 0

    return agreeCount > disagreeCount && agreeCount > otherCount
  }

  const generateDocx = () => {
    const nameMap = buildNameMap()
    const positionMap = buildPositionMap()
    const positionByNameMap = buildPositionByNameMap()
    const allVoters = getAllVoters(nameMap)
    const voterVotesMap = buildVoterVotesMap()
    const numContents = feedbackComments?.length || 0

    const totalVoted =
      results?.reduce((sum, r) => {
        const voted =
          r.countAgree + r.countDisAgree + (isDelegateTab ? 0 : r.countOther)
        return Math.max(sum, voted)
      }, 0) || 0

    const approvedContents = (feedbackComments || [])
      .map((content, idx) => ({
        content,
        result: results?.[idx] || {
          countAgree: 0,
          countDisAgree: 0,
          countOther: 0,
        },
      }))
      .filter(item => isApprovedContent(item.result))

    const sectionIParagraphs = (feedbackComments || []).flatMap(
      (comment, idx) => {
        const contentLines = htmlToPlainLines(comment.content || '')
        const contentParagraphs = contentLines.map((line, lineIdx) => {
          const isLastLine = lineIdx === contentLines.length - 1

          return new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: isLastLine ? 100 : 60, line: 360 },
            indent: { left: 360 },
            children: [
              new TextRun({
                text: line,
                size: 26,
                font: 'Times New Roman',
              }),
            ],
          })
        })

        return [
          new Paragraph({
            alignment: AlignmentType.LEFT,
            spacing: { after: contentParagraphs.length ? 40 : 100, line: 360 },
            indent: { left: 360 },
            children: [
              new TextRun({
                text: `Nội dung ${idx + 1}:`,
                bold: true,
                size: 26,
                font: 'Times New Roman',
              }),
            ],
          }),
          ...contentParagraphs,
        ]
      }
    )

    // ── Section II: Per-voter, per-content votes ──────────────────────────────
    const sectionIIParagraphs = allVoters.flatMap((voter, voterIdx) => {
      const contentVotes = voterVotesMap[voter.feedbackUsername] || {}
      const voterName = getVoterName(voter, nameMap)

      // Voter name line
      const namePara = new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 0, line: 360 },
        indent: { left: 360 },
        children: [
          new TextRun({
            text: `${voterIdx + 1}. Ông ${voterName}:`,
            size: 26,
            font: 'Times New Roman',
          }),
        ],
      })

      // Per-content vote lines (opening " on first, closing " on last)
      const contentParas = Array.from(
        { length: numContents },
        (_, contentIdx) => {
          const fb = contentVotes[contentIdx]
          const voteText = !fb
            ? ''
            : fb.voteType === 'AGREE'
            ? 'Tán thành.'
            : fb.voteType === 'DISAGREE'
            ? 'Không tán thành.'
            : isDelegateTab
            ? 'Không tán thành.'
            : 'Không có ý kiến.'
          const opinion = fb?.opinion || fb?.comment || ''
          const isFirst = contentIdx === 0
          const isLast = contentIdx === numContents - 1

          return new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: isLast ? 120 : 0, line: 360 },
            indent: { left: 720 },
            children: [
              ...(isFirst
                ? [
                    new TextRun({
                      text: '\u201c',
                      size: 26,
                      font: 'Times New Roman',
                      italics: true,
                    }),
                  ]
                : []),
              new TextRun({
                text: `Nội dung ${contentIdx + 1}: ${voteText}${
                  opinion ? ' ' + opinion : ''
                }`,
                size: 26,
                font: 'Times New Roman',
                italics: true,
              }),
              ...(isLast
                ? [
                    new TextRun({
                      text: '\u201d',
                      size: 26,
                      font: 'Times New Roman',
                      italics: true,
                    }),
                  ]
                : []),
            ],
          })
        }
      )

      return [namePara, ...contentParas]
    })

    // ── Section III – per-content breakdown ──────────────────────────────────
    const sectionIIIContentParagraphs = (feedbackComments || []).flatMap(
      (_, idx) => {
        const result = results?.[idx] || {
          countAgree: 0,
          countDisAgree: 0,
          countOther: 0,
        }
        const contentAgreeVoters =
          resultsVoting?.[idx]
            ?.filter(fb => fb.voteType === 'AGREE')
            ?.sort((a, b) => compareVoters(a, b, nameMap)) || []
        const agreeNamesForContent = contentAgreeVoters
          .map(v => getVoterName(v, nameMap))
          .join(', ')
        const agreeRatio =
          totalPeopleCount > 0
            ? ((result.countAgree / totalPeopleCount) * 100).toFixed(0)
            : 0

        return [
          // "Nội dung N:"
          new Paragraph({
            alignment: AlignmentType.LEFT,
            spacing: { before: 100, after: 60, line: 360 },
            indent: { left: 720 },
            children: [
              new TextRun({
                text: `Nội dung ${idx + 1}:`,
                size: 26,
                font: 'Times New Roman',
              }),
            ],
          }),
          // Tán thành
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 60, line: 360 },
            indent: { left: 1080 },
            children: [
              new TextRun({
                text: '- Số phiếu tán thành: ',
                size: 26,
                font: 'Times New Roman',
              }),
              new TextRun({
                text: `${formatNumber(result.countAgree)}/${formatNumber(
                  totalPeopleCount
                )}`,
                bold: true,
                size: 26,
                font: 'Times New Roman',
              }),
              new TextRun({
                text: ` (gồm các ông: ${agreeNamesForContent}) đạt tỷ lệ `,
                size: 26,
                font: 'Times New Roman',
              }),
              new TextRun({
                text: `${agreeRatio}%`,
                bold: true,
                size: 26,
                font: 'Times New Roman',
              }),
              new TextRun({
                text: ' tổng số phiếu biểu quyết.',
                size: 26,
                font: 'Times New Roman',
              }),
            ],
          }),
          // Không tán thành
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 60, line: 360 },
            indent: { left: 1080 },
            children: [
              new TextRun({
                text: '- Số phiếu không tán thành: ',
                size: 26,
                font: 'Times New Roman',
              }),
              new TextRun({
                text: `${result.countDisAgree}`,
                bold: true,
                size: 26,
                font: 'Times New Roman',
              }),
              new TextRun({
                text: ' (không phiếu).',
                size: 26,
                font: 'Times New Roman',
              }),
            ],
          }),
          ...(!isDelegateTab
            ? [
                // Không có ý kiến
                new Paragraph({
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 100, line: 360 },
                  indent: { left: 1080 },
                  children: [
                    new TextRun({
                      text: '- Số phiếu không có ý kiến: ',
                      size: 26,
                      font: 'Times New Roman',
                    }),
                    new TextRun({
                      text: `${result.countOther}`,
                      bold: true,
                      size: 26,
                      font: 'Times New Roman',
                    }),
                    new TextRun({
                      text: ' (không phiếu).',
                      size: 26,
                      font: 'Times New Roman',
                    }),
                  ],
                }),
              ]
            : []),
        ]
      }
    )

    // ── Document ──────────────────────────────────────────────────────────────
    const allAgreeVoters = allVoters
      .filter(voter =>
        resultsVoting?.some(feedbackList =>
          feedbackList?.some(
            fb =>
              fb.feedbackUsername === voter.feedbackUsername &&
              fb.voteType === 'AGREE'
          )
        )
      )
      .sort((a, b) => compareVoters(a, b, nameMap))
    const allAgreeNames = allAgreeVoters
      .map(voter => getVoterName(voter, nameMap))
      .join(', ')

    const doc = new Document({
      styles: {
        default: {
          document: { run: { font: 'Times New Roman', size: 26 } },
        },
      },
      sections: [
        {
          properties: {
            page: {
              size: { width: 11906, height: 16838 },
              margin: { top: 1134, right: 851, bottom: 1134, left: 1701 },
            },
          },
          footers: {
            default: new Footer({
              children: isDelegateTab
                ? [
                    new Paragraph({
                      alignment: AlignmentType.LEFT,
                      spacing: { before: 120, after: 0 },
                      border: {
                        top: {
                          style: BorderStyle.SINGLE,
                          size: 2,
                          color: '888888',
                        },
                      },
                      children: [
                        new TextRun({
                          text:
                            'Dự thảo Báo cáo Bộ Tài chính của Người đại diện về việc điều chỉnh tỷ lệ vốn nhà nước và thông qua chủ trương tăng vốn điều lệ VIMC giai đoạn 2025-2030',
                          italics: true,
                          color: '777777',
                          size: 24,
                          font: 'Times New Roman',
                        }),
                      ],
                    }),
                    new Paragraph({
                      alignment: AlignmentType.CENTER,
                      spacing: { before: 0, after: 0 },
                      children: [
                        new TextRun({
                          children: [PageNumber.CURRENT],
                          size: 24,
                          font: 'Times New Roman',
                        }),
                      ],
                    }),
                  ]
                : [],
            }),
          },
          children: [
            // ── Header table ──────────────────────────────────────────────────
            new Table({
              width: { size: 9354, type: WidthType.DXA },
              columnWidths: [4000, 5354],
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      borders: noBorder,
                      width: { size: 4000, type: WidthType.DXA },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: [
                            new TextRun({
                              text: 'TỔNG CÔNG TY',
                              bold: true,
                              size: 24,
                              font: 'Times New Roman',
                            }),
                            new TextRun({
                              text: 'HÀNG HẢI VIỆT NAM-CTCP',
                              bold: true,
                              size: 24,
                              font: 'Times New Roman',
                              break: 1,
                            }),
                          ],
                        }),
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: '___________',
                              size: 24,
                              font: 'Times New Roman',
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      borders: noBorder,
                      width: { size: 5354, type: WidthType.DXA },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: 'CỘNG HOÀ XÃ HỘI CHỦ NGHĨA VIỆT NAM',
                              bold: true,
                              size: 24,
                              font: 'Times New Roman',
                            }),
                            new TextRun({
                              text: 'Độc lập - Tự do - Hạnh phúc',
                              bold: true,
                              size: 24,
                              font: 'Times New Roman',
                              break: 1,
                            }),
                          ],
                        }),
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: '_________________',
                              bold: true,
                              size: 24,
                              font: 'Times New Roman',
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      borders: noBorder,
                      width: { size: 4000, type: WidthType.DXA },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: `Số: ${displayDocumentNumber}/${
                                isDelegateTab ? thykSuffix : bbykSuffix
                              }`,
                              size: 24,
                              font: 'Times New Roman',
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      borders: noBorder,
                      width: { size: 5354, type: WidthType.DXA },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: `Hà Nội, ngày ${dd} tháng ${mm} năm ${yyyy}`,
                              italics: true,
                              size: 24,
                              font: 'Times New Roman',
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),

            // ── Tiêu đề ──────────────────────────────────────────────────────
            new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 600, after: 0 },
              children: [
                new TextRun({
                  text: isDelegateTab ? 'BÁO CÁO' : 'BIÊN BẢN',
                  bold: true,
                  size: 28,
                  font: 'Times New Roman',
                }),
              ],
            }),
            new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 0, after: 0 },
              children: [
                new TextRun({
                  text: isDelegateTab
                    ? 'Tổng hợp phiếu ý kiến Người đại diện phần vốn nhà nước'
                    : 'Tổng hợp phiếu lấy ý kiến thành viên Hội đồng quản trị',
                  bold: true,
                  size: 26,
                  font: 'Times New Roman',
                  underline: { type: UnderlineType.NONE },
                }),
              ],
            }),
            new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 0, after: 300 },
              children: [
                new TextRun({
                  text: isDelegateTab
                    ? 'tại Tổng công ty Hàng hải Việt Nam - CTCP'
                    : 'Tổng công ty Hàng hải Việt Nam - CTCP',
                  bold: true,
                  size: 26,
                  font: 'Times New Roman',
                  underline: { type: UnderlineType.NONE },
                }),
              ],
            }),

            // ── Đoạn mở đầu ──────────────────────────────────────────────────
            new Paragraph({
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
              indent: { firstLine: 720 },
              children: [
                new TextRun({
                  text: isDelegateTab
                    ? 'Căn cứ các quy định của pháp luật và của Bộ Tài chính, Người đại diện phụ trách chung đã tổ chức lấy ý kiến Người đại diện tại Phiếu lấy ý kiến số '
                    : 'Ban TK-TH thực hiện việc tổng hợp Phiếu lấy ý kiến số ',
                  size: 26,
                  font: 'Times New Roman',
                }),
                new TextRun({
                  text: `${displayDocumentNumber}/${plykSuffix}`,
                  size: 26,
                  font: 'Times New Roman',
                }),
                new TextRun({
                  text: ' ngày ',
                  size: 26,
                  font: 'Times New Roman',
                }),
                new TextRun({
                  text: feedbackDetailData.startDate
                    ? new Date(feedbackDetailData.startDate).toLocaleDateString(
                        'vi-VN'
                      )
                    : '',
                  size: 26,
                  font: 'Times New Roman',
                }),
                new TextRun({
                  text: ' về việc ',
                  size: 26,
                  font: 'Times New Roman',
                }),
                new TextRun({
                  text: title,
                  size: 26,
                  font: 'Times New Roman',
                }),
                new TextRun({
                  text: ', kết quả như sau:',
                  size: 26,
                  font: 'Times New Roman',
                }),
              ],
            }),

            // ── I. Nội dung lấy ý kiến ────────────────────────────────────────
            new Paragraph({
              alignment: AlignmentType.LEFT,
              spacing: { before: 100, after: 100 },
              children: [
                new TextRun({
                  text: isDelegateTab
                    ? 'I. Vấn đề lấy ý kiến:'
                    : 'I. Nội dung lấy ý kiến: ',
                  bold: true,
                  size: 26,
                  font: 'Times New Roman',
                }),
              ],
            }),

            ...sectionIParagraphs,

            ...(isDelegateTab
              ? [
                  new Paragraph({
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { before: 0, after: 100, line: 360 },
                    indent: { left: 360 },
                    children: [
                      new TextRun({
                        text: 'Tài liệu kèm theo: ',
                        bold: true,
                        size: 26,
                        font: 'Times New Roman',
                      }),
                      new TextRun({
                        text: attachedDocumentsText || '...',
                        size: 26,
                        font: 'Times New Roman',
                      }),
                    ],
                  }),
                ]
              : []),

            // ── II. Ý kiến của các thành viên HĐQT ──────────────────────────
            new Paragraph({
              alignment: AlignmentType.LEFT,
              spacing: { before: 200, after: 100 },
              children: [
                new TextRun({
                  text: isDelegateTab
                    ? 'II. Ý kiến của Người đại diện:'
                    : 'II. Ý kiến của các thành viên HĐQT:',
                  bold: true,
                  size: 26,
                  font: 'Times New Roman',
                  underline: { type: UnderlineType.NONE },
                }),
              ],
            }),

            ...sectionIIParagraphs,

            // ── III. Kết quả kiểm phiếu ──────────────────────────────────────
            new Paragraph({
              alignment: AlignmentType.LEFT,
              spacing: { before: 200, after: 100 },
              children: [
                new TextRun({
                  text: isDelegateTab
                    ? 'III. Kết quả:'
                    : 'III. Kết quả kiểm phiếu:',
                  bold: true,
                  size: 26,
                  font: 'Times New Roman',
                  underline: { type: UnderlineType.NONE },
                }),
              ],
            }),

            ...(isDelegateTab
              ? [
                  new Paragraph({
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 120, line: 360 },
                    indent: { left: 360 },
                    children: [
                      new TextRun({
                        text: `${formatNumber(
                          allAgreeVoters.length
                        )}/${formatNumber(totalPeopleCount)} phiếu tán thành`,
                        size: 26,
                        font: 'Times New Roman',
                      }),
                      new TextRun({
                        text: allAgreeNames
                          ? ` (gồm các ông: ${allAgreeNames})`
                          : '',
                        size: 26,
                        font: 'Times New Roman',
                      }),
                    ],
                  }),
                ]
              : [
                  // 1. Tổng số phiếu
                  new Paragraph({
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 100, line: 360 },
                    indent: { left: 360 },
                    children: [
                      new TextRun({
                        text: '1.  ',
                        bold: true,
                        size: 26,
                        font: 'Times New Roman',
                      }),
                      new TextRun({
                        text: 'Tổng số phiếu tham gia biểu quyết: ',
                        bold: true,
                        size: 26,
                        font: 'Times New Roman',
                        underline: { type: UnderlineType.NONE },
                      }),
                      new TextRun({
                        text: `${formatNumber(totalVoted)} phiếu`,
                        size: 26,
                        font: 'Times New Roman',
                      }),
                      new TextRun({
                        text: `, trong đó có ${formatNumber(
                          totalVoted
                        )} phiếu biểu quyết hợp lệ và 0 phiếu biểu quyết không hợp lệ.`,
                        size: 26,
                        font: 'Times New Roman',
                      }),
                    ],
                  }),

                  // 2. Tỷ lệ biểu quyết
                  new Paragraph({
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 100, line: 360 },
                    indent: { left: 360 },
                    children: [
                      new TextRun({
                        text: '2. ',
                        bold: true,
                        size: 26,
                        font: 'Times New Roman',
                      }),
                      new TextRun({
                        text: 'Tỷ lệ biểu quyết:',
                        bold: true,
                        size: 26,
                        font: 'Times New Roman',
                        underline: { type: UnderlineType.NONE },
                      }),
                    ],
                  }),

                  // Per-content breakdown
                  ...sectionIIIContentParagraphs,
                ]),

            ...(!isDelegateTab
              ? [
                  // ── IV. Các nội dung HĐQT thông qua ─────────────────────────
                  new Paragraph({
                    alignment: AlignmentType.LEFT,
                    spacing: { before: 100, after: 100 },
                    children: [
                      new TextRun({
                        text: 'IV. Các nội dung HĐQT thông qua:',
                        bold: true,
                        size: 26,
                        font: 'Times New Roman',
                        underline: { type: UnderlineType.NONE },
                      }),
                    ],
                  }),

                  ...approvedContents.flatMap((item, approvedIdx) => [
                    new Paragraph({
                      alignment: AlignmentType.JUSTIFIED,
                      spacing: { after: 100, line: 360 },
                      indent: { firstLine: 720 },
                      children: [
                        new TextRun({
                          text: `Điều ${approvedIdx + 1}. `,
                          bold: true,
                          size: 26,
                          font: 'Times New Roman',
                        }),
                        new TextRun({
                          text: `${stripHtml(item.content.content || '')}.`,
                          size: 26,
                          font: 'Times New Roman',
                        }),
                      ],
                    }),
                  ]),

                  // Final Điều (Giao TGĐ...)
                  new Paragraph({
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 100, line: 360 },
                    indent: { firstLine: 720 },
                    children: [
                      new TextRun({
                        text: `Điều ${(approvedContents?.length || 0) + 1}. `,
                        bold: true,
                        size: 26,
                        font: 'Times New Roman',
                      }),
                      new TextRun({
                        text: `Giao Tổng giám đốc Tổng công ty tổ chức triển khai thực hiện Nghị quyết của Hội đồng quản trị phù hợp các quy định hiện hành có liên quan.`,
                        size: 26,
                        font: 'Times New Roman',
                      }),
                    ],
                  }),
                ]
              : []),

            // Lưu biên bản
            new Paragraph({
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
              indent: { firstLine: 720 },
              children: [
                new TextRun({
                  text: isDelegateTab
                    ? ``
                    : 'Biên bản được lưu tại Ban TK-TH và gửi các thành viên HĐQT, Ban Kiểm soát Tổng công ty./.',
                  size: 26,
                  font: 'Times New Roman',
                  italics: isDelegateTab,
                }),
              ],
            }),

            // ── Bảng chữ ký ──────────────────────────────────────────────────
            ...(!isDelegateTab
              ? [
                  new Table({
                    width: { size: 9354, type: WidthType.DXA },
                    columnWidths: [5100, 4254],
                    rows: [
                      new TableRow({
                        children: [
                          new TableCell({
                            borders: noBorder,
                            width: { size: 5100, type: WidthType.DXA },
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                  new TextRun({
                                    text: isDelegateTab ? '' : 'CHỦ TỊCH HĐQT',
                                    bold: true,
                                    size: 26,
                                    font: 'Times New Roman',
                                  }),
                                ],
                              }),
                            ],
                          }),
                          new TableCell({
                            borders: noBorder,
                            width: { size: 4254, type: WidthType.DXA },
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                  new TextRun({
                                    text: isDelegateTab
                                      ? ''
                                      : 'TRƯỞNG BAN TK-TH',
                                    bold: true,
                                    size: 26,
                                    font: 'Times New Roman',
                                  }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      }),
                      new TableRow({
                        children: [
                          new TableCell({
                            borders: noBorder,
                            width: { size: 5100, type: WidthType.DXA },
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { before: 1200 },
                                children: [
                                  new TextRun({
                                    text: isDelegateTab
                                      ? ''
                                      : 'Nguyễn Cảnh Tĩnh',
                                    bold: true,
                                    size: 26,
                                    font: 'Times New Roman',
                                  }),
                                ],
                              }),
                            ],
                          }),
                          new TableCell({
                            borders: noBorder,
                            width: { size: 4254, type: WidthType.DXA },
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { before: 1200 },
                                children: [
                                  new TextRun({
                                    text: isDelegateTab ? '' : 'Trần Tuấn Hải',
                                    bold: true,
                                    size: 26,
                                    font: 'Times New Roman',
                                  }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                ]
              : []),

            ...(isDelegateTab
              ? [
                  new Paragraph({
                    alignment: AlignmentType.CENTER,
                    spacing: { before: 600, after: 200 },
                    children: [
                      new TextRun({
                        text:
                          'Người đại diện phần vốn nhà nước tại Tổng công ty',
                        bold: true,
                        size: 32,
                        font: 'Times New Roman',
                      }),
                      new TextRun({
                        text: 'Hàng hải Việt Nam - CTCP',
                        bold: true,
                        size: 32,
                        font: 'Times New Roman',
                        break: 1,
                      }),
                    ],
                  }),
                  new Table({
                    width: { size: 9354, type: WidthType.DXA },
                    columnWidths: [2500, 3000, 3854],
                    rows: [
                      new TableRow({
                        children: [
                          new TableCell({
                            borders: noBorder,
                            width: { size: 2500, type: WidthType.DXA },
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                  new TextRun({
                                    text: 'Họ và tên',
                                    bold: true,
                                    size: 28,
                                    font: 'Times New Roman',
                                  }),
                                ],
                              }),
                            ],
                          }),
                          new TableCell({
                            borders: noBorder,
                            width: { size: 3500, type: WidthType.DXA },
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                  new TextRun({
                                    text: 'Chức vụ',
                                    bold: true,
                                    size: 28,
                                    font: 'Times New Roman',
                                  }),
                                ],
                              }),
                            ],
                          }),
                          new TableCell({
                            borders: noBorder,
                            width: { size: 3354, type: WidthType.DXA },
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                  new TextRun({
                                    text: 'Chữ ký',
                                    bold: true,
                                    size: 28,
                                    font: 'Times New Roman',
                                  }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      }),
                      ...allVoters.map(voter => {
                        const voterName = getVoterName(voter, nameMap) || ''
                        const voterPosition = getVoterPosition(
                          voter,
                          positionMap,
                          positionByNameMap,
                          nameMap
                        )
                        return new TableRow({
                          children: [
                            new TableCell({
                              borders: noBorder,
                              width: { size: 2500, type: WidthType.DXA },
                              children: [
                                new Paragraph({
                                  alignment: AlignmentType.LEFT,
                                  spacing: { before: 200, after: 200 },
                                  children: [
                                    new TextRun({
                                      text: voterName,
                                      size: 28,
                                      font: 'Times New Roman',
                                    }),
                                  ],
                                }),
                              ],
                            }),
                            new TableCell({
                              borders: noBorder,
                              width: { size: 3500, type: WidthType.DXA },
                              children: [
                                new Paragraph({
                                  alignment: AlignmentType.CENTER,
                                  spacing: { before: 200, after: 200 },
                                  children: [
                                    new TextRun({
                                      text: voterPosition,
                                      size: 28,
                                      font: 'Times New Roman',
                                    }),
                                  ],
                                }),
                              ],
                            }),
                            new TableCell({
                              borders: noBorder,
                              width: { size: 3354, type: WidthType.DXA },
                              children: [new Paragraph({})],
                            }),
                          ],
                        })
                      }),
                    ],
                  }),
                ]
              : []),
          ],
        },
      ],
    })

    Packer.toBlob(doc).then(blob => {
      saveAs(
        blob,
        `${
          isDelegateTab ? 'Bao_cao_tong_hop_y_kien' : 'Bien_ban_lay_y_kien'
        }_${title}.docx`
      )
    })
  }

  return <div>{generateDocx()}</div>
}

export default ExportDocx
